import { createClient } from "@supabase/supabase-js";
import { getSupabaseUrl, getSupabaseAnonKey } from "./config.js";
import { toErrorDisplayMessage } from "@/lib/utils";
import { accessTokenFrom, sessionIdentity } from "@/lib/session-token";

/** Supabase liefert `error.message` meist als String; absichern gegen Objekte (Toast/React → ToPrimitive). */
function supabaseAuthErrorMessage(error: { message?: unknown } | null | undefined): string | null {
  if (!error) return null;
  const m = toErrorDisplayMessage(error.message);
  return m.trim() ? m : null;
}

export type AuthListener = (token: string | null, email: string | null, userId: string | null) => void;

let supabaseClient: ReturnType<typeof createClient> | null = null;

export function getSupabase() {
  if (!supabaseClient) {
    supabaseClient = createClient(getSupabaseUrl(), getSupabaseAnonKey());
  }
  return supabaseClient;
}

const TOKEN_TIMEOUT_MS = 10_000;
const AUTH_REQUEST_TIMEOUT_MS = 15_000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
  return Promise.race([
    promise,
    new Promise<null>((resolve) => setTimeout(() => resolve(null), ms)),
  ]);
}

function withTimeoutReject<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(message)), ms)
    ),
  ]);
}

export async function getToken(): Promise<string | null> {
  const url = getSupabaseUrl();
  const key = getSupabaseAnonKey();
  if (!url || !key || url.includes("placeholder")) return null;
  try {
    const data = await withTimeout(
      getSupabase().auth.getSession().then((r) => r.data),
      TOKEN_TIMEOUT_MS,
    );
    return accessTokenFrom(data?.session);
  } catch {
    return null;
  }
}

export function onAuthChange(cb: AuthListener): () => void {
  const url = getSupabaseUrl();
  const key = getSupabaseAnonKey();
  // Every call below goes through sessionIdentity, including the "nothing to report" ones:
  // sessionIdentity(null) IS (null, null, null), and one producer means these four sites cannot
  // drift apart. None of them is reachable from a test — that is the whole reason the
  // construction was moved into lib/session-token.ts.
  if (!url || !key || url.includes("placeholder")) {
    cb(...sessionIdentity(null));
    return () => {};
  }
  getSupabase()
    .auth.getSession()
    .then((r) => cb(...sessionIdentity(r.data.session)))
    .catch(() => cb(...sessionIdentity(null)));
  let unsub: (() => void) | null = null;
  try {
    const {
      data: { subscription },
    } = getSupabase().auth.onAuthStateChange((_event, session) => {
      cb(...sessionIdentity(session));
    });
    unsub = () => subscription.unsubscribe();
  } catch {
    cb(...sessionIdentity(null));
  }
  return () => unsub?.();
}

export async function signIn(email: string, password: string): Promise<{ error: string | null }> {
  try {
    const { error } = await withTimeoutReject(
      getSupabase().auth.signInWithPassword({ email, password }),
      AUTH_REQUEST_TIMEOUT_MS,
      "Anmeldung hat zu lange gedauert. Bitte Netzwerk und Supabase-URL prüfen."
    );
    return { error: supabaseAuthErrorMessage(error) };
  } catch (e) {
    return { error: e instanceof Error ? e.message : "Anmeldung fehlgeschlagen." };
  }
}

export async function signUp(email: string, password: string): Promise<{ error: string | null }> {
  try {
    const { error } = await withTimeoutReject(
      getSupabase().auth.signUp({ email, password }),
      AUTH_REQUEST_TIMEOUT_MS,
      "Registrierung hat zu lange gedauert. Bitte Netzwerk und Supabase-URL prüfen."
    );
    return { error: supabaseAuthErrorMessage(error) };
  } catch (e) {
    return { error: e instanceof Error ? e.message : "Registrierung fehlgeschlagen." };
  }
}

export async function signInWithMagicLink(email: string): Promise<{ error: string | null }> {
  const redirectTo = typeof window !== "undefined" ? window.location.origin : "";
  try {
    const { error } = await withTimeoutReject(
      getSupabase().auth.signInWithOtp({
        email: email.trim(),
        options: redirectTo ? { emailRedirectTo: redirectTo } : undefined,
      }),
      AUTH_REQUEST_TIMEOUT_MS,
      "Magic Link konnte nicht gesendet werden. Bitte später erneut versuchen."
    );
    return { error: supabaseAuthErrorMessage(error) };
  } catch (e) {
    return { error: e instanceof Error ? e.message : "Anfrage fehlgeschlagen." };
  }
}

export type OAuthProvider = "google" | "apple" | "github" | "azure" | "facebook";

export async function signInWithOAuth(
  provider: OAuthProvider,
  redirectTo?: string
): Promise<{ error: string | null }> {
  const baseUrl =
    typeof window !== "undefined"
      ? window.location.origin
      : "";
  const callbackPath = "/auth/callback";
  const redirectToUrl = redirectTo ?? (baseUrl ? `${baseUrl}${callbackPath}` : "");
  if (!redirectToUrl) {
    return { error: "Redirect-URL für OAuth nicht verfügbar." };
  }
  try {
    const { error } = await withTimeoutReject(
      getSupabase().auth.signInWithOAuth({
        provider,
        options: { redirectTo: redirectToUrl },
      }),
      AUTH_REQUEST_TIMEOUT_MS,
      "Weiterleitung zum Anbieter hat zu lange gedauert."
    );
    return { error: supabaseAuthErrorMessage(error) };
  } catch (e) {
    return { error: e instanceof Error ? e.message : "OAuth-Anmeldung fehlgeschlagen." };
  }
}

/**
 * Callback nach OAuth-Redirect: Code aus URL tauschen und Session setzen.
 * Sollte auf der /auth/callback-Seite aufgerufen werden.
 */
export async function exchangeCodeForSession(): Promise<{ error: string | null }> {
  if (typeof window === "undefined") return { error: "Nur im Browser verfügbar." };
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const errorParam = params.get("error");
  const errorDescription = params.get("error_description");
  if (errorParam) {
    const oauthErr = toErrorDisplayMessage(errorDescription ?? errorParam);
    return { error: oauthErr.trim() ? oauthErr : errorParam };
  }
  if (!code) {
    return { error: "Kein OAuth-Code in der URL." };
  }
  try {
    const { error } = await withTimeoutReject(
      getSupabase().auth.exchangeCodeForSession(code),
      AUTH_REQUEST_TIMEOUT_MS,
      "Session konnte nicht hergestellt werden."
    );
    return { error: supabaseAuthErrorMessage(error) };
  } catch (e) {
    return { error: e instanceof Error ? e.message : "OAuth-Callback fehlgeschlagen." };
  }
}

export async function signOut(): Promise<void> {
  await getSupabase().auth.signOut();
}
